
/**
 * Facebook Pixel Standard Events Reference:
 * - PageView: Track page views (auto-tracked)
 * - ViewContent: Track product/content views
 * - Search: Track search queries
 * - AddToCart: Track when items added to cart
 * - AddToWishlist: Track when items added to wishlist
 * - InitiateCheckout: Track checkout initiations
 * - AddPaymentInfo: Track payment info submissions
 * - Purchase: Track completed purchases
 * - Lead: Track leads/sign-ups
 * - CompleteRegistration: Track registration completions
 */

/**
 * Safely track Facebook Pixel events with retry mechanism.
 * Ensures events are fired even if the Pixel script loads slightly after the component.
 */
export const trackFacebookEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 50; // 5 seconds (with 100ms interval)
  let attempts = 0;

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        // Add event_id for deduplication
        const eventData = {
          ...data,
          event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        window.fbq('track', eventName, eventData);
        console.log(`FB Pixel Event Tracked: ${eventName}`, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        console.log(`FB Pixel not ready, retrying (${attempts}/${MAX_RETRIES})...`);
        setTimeout(track, 100);
      } else {
        console.warn(`FB Pixel Event Timed Out: ${eventName}`);
        resolve(); // Resolve anyway so we don't block navigation
      }
    };

    track();
  });
};

/**
 * Track a custom event (non-standard Facebook event)
 */
export const trackCustomEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 50;
  let attempts = 0;

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        const eventData = {
          ...data,
          event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        window.fbq('trackCustom', eventName, eventData);
        console.log(`FB Pixel Custom Event Tracked: ${eventName}`, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        console.warn(`FB Pixel Custom Event Timed Out: ${eventName}`);
        resolve();
      }
    };

    track();
  });
};

/**
 * Predefined event helpers for common e-commerce actions
 */
export const fbEvents = {
  viewContent: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
  }) => trackFacebookEvent('ViewContent', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT',
    content_category: product.category || ''
  }),

  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
  }) => trackFacebookEvent('AddToCart', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price * (product.quantity || 1),
    currency: 'BDT',
    num_items: product.quantity || 1
  }),

  addToWishlist: (product: {
    id: string | number;
    name: string;
    price: number;
  }) => trackFacebookEvent('AddToWishlist', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT'
  }),

  initiateCheckout: (items: Array<{
    id: string | number;
    quantity: number;
  }>, totalValue: number) => trackFacebookEvent('InitiateCheckout', {
    content_ids: items.map(item => item.id.toString()),
    content_type: 'product',
    value: totalValue,
    currency: 'BDT',
    num_items: items.reduce((acc, item) => acc + item.quantity, 0)
  }),

  purchase: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number }>;
    totalValue: number;
  }) => trackFacebookEvent('Purchase', {
    content_ids: order.items.map(item => item.id.toString()),
    content_type: 'product',
    value: order.totalValue,
    currency: 'BDT',
    num_items: order.items.reduce((acc, item) => acc + item.quantity, 0),
    order_id: order.orderId
  }),

  search: (searchQuery: string) => trackFacebookEvent('Search', {
    search_string: searchQuery,
    content_type: 'product'
  })
};
